import { useId, useCallback, forwardRef } from 'react'
import { useTranslation } from 'next-i18next'
import styled from '@emotion/styled'
import { errorStyles, inputStyles } from 'css/utils'
import { InputProps } from 'modules/ui/atoms/Input'
import { InputContainer } from 'modules/ui/atoms/Input/InputContainer'

export const StyledTextArea = styled.textarea(
  {
    minHeight: '48px',
    resize: 'vertical',
    verticalAlign: 'top',
    ...inputStyles,
  },
  errorStyles,
)

export const TextArea = forwardRef<HTMLTextAreaElement, InputProps>((props, ref) => {
  const { t } = useTranslation()

  const { onChange, onBlur, value, placeholder, error, maxLength, tabIndex, autoFocus, dataId } =
    props

  const id = useId()
  const captionId = useId()

  const maxLengthError = maxLength && value && value.length > maxLength
  let maxLengthErrorText = ''

  if (maxLengthError) {
    const extraCharacters = value.length - maxLength

    maxLengthErrorText = t('TextArea.overLimit', {
      extraCharacters,
      defaultValue: `You are {{extraCharacters}} character${
        extraCharacters > 1 ? 's' : ''
      } over the limit.`,
    })
  }

  const onChangeCallback = useCallback(
    (e: { target: { value: string } }) => {
      onChange(e.target.value)
    },
    [onChange],
  )

  return (
    <InputContainer
      {...props}
      id={id}
      captionId={captionId}
      error={maxLengthError ? maxLengthErrorText : error}
    >
      <StyledTextArea
        errored={!!error || !!maxLengthError}
        onChange={onChangeCallback}
        onBlur={onBlur}
        ref={ref}
        value={value}
        placeholder={placeholder}
        id={id}
        aria-describedby={captionId}
        aria-invalid={!!error ? 'true' : 'false'}
        tabIndex={tabIndex}
        autoFocus={autoFocus}
        data-id={dataId}
      />
    </InputContainer>
  )
})

TextArea.displayName = 'TextArea'
